/** Sequences sidebar (spec §8.11): create, rename, reorder members, open a member. */
import { GripVertical, ListPlus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { describeNodes } from "../api/hooks";
import { useNavigate } from "@tanstack/react-router";
import { parseIri } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useT } from "../i18n";
import { createSequence, deleteSequence, moveMember, readSequences, removeMember, renameSequence, type Sequence } from "./model";

const NO_SEQUENCES: Sequence[] = [];

export function SequencesSidebar({ wsHandle, workspaceId, readOnly }: { wsHandle: DocHandle | null; workspaceId: string; readOnly: boolean }) {
  const t = useT();
  const sequences =
    useLoroSelector(wsHandle?.doc ?? null, readSequences, (a, b) => JSON.stringify(a) === JSON.stringify(b)) ?? NO_SEQUENCES;
  const [label, setLabel] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  // members by their block's label (spec §8.11 [0.49]); offline, the block id as before
  const targets = useMemo(() => [...new Set(sequences.flatMap((s) => s.members.map((m) => m.target)))].sort(), [sequences]);
  const labels = useQuery({
    queryKey: ["sequenceLabels", workspaceId, targets],
    enabled: targets.length > 0,
    staleTime: 60_000,
    queryFn: async () => new Map((await describeNodes(workspaceId, targets)).map((d) => [d.iri, d.label])),
  });
  const navigate = useNavigate();

  return (
    <section className="kss-surface p-4" data-testid="sequences-sidebar">
      <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{t("seq.title")}</h2>
      {sequences.length === 0 && <p className="text-neutral-500">{t("seq.empty")}</p>}
      <ul className="space-y-1">
        {sequences.map((s) => (
          <li key={s.id}>
            <div className="flex items-center gap-1">
              <button className="grow truncate text-left underline" onClick={() => setOpen(open === s.id ? null : s.id)} data-testid="sequence-item">
                {s.label || t("seq.untitled")} <span className="text-xs text-neutral-500">({s.members.length})</span>
              </button>
              {!readOnly && (
                <button className="text-neutral-400 hover:text-red-700" title={t("seq.delete")} onClick={() => wsHandle && deleteSequence(wsHandle.doc, s.id)}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            {open === s.id && (
              <div className="ml-2 mt-1 space-y-1 border-l border-neutral-200 pl-2 dark:border-neutral-800">
                {!readOnly && (
                  <Input
                    defaultValue={s.label}
                    aria-label={t("seq.name")}
                    data-testid="sequence-rename"
                    onBlur={(e) => wsHandle && e.target.value !== s.label && renameSequence(wsHandle.doc, s.id, e.target.value)}
                  />
                )}
                {s.members.map((m, i) => {
                  const parsed = parseIri(m.target);
                  return (
                    <div key={m.memberId} className="flex items-center gap-1 text-xs" data-testid="sequence-member">
                      <GripVertical size={12} className="text-neutral-400" />
                      <button
                        className="grow truncate text-left underline"
                        onClick={() => parsed?.doc && navigate({ to: "/w/$workspaceId/d/$docId", params: { workspaceId, docId: parsed.doc }, hash: `block=${parsed.treeId}` })}
                      >
                        {labels.data?.get(m.target) || (parsed?.kind === "block" ? t("seq.block", { id: parsed.treeId ?? "" }) : m.target)}
                      </button>
                      {!readOnly && (
                        <>
                          <button className="px-1" title={t("seq.moveUp")} disabled={i === 0} onClick={() => wsHandle && moveMember(wsHandle.doc, s.id, i, i - 1)}>↑</button>
                          <button className="px-1" title={t("seq.moveDown")} disabled={i === s.members.length - 1} onClick={() => wsHandle && moveMember(wsHandle.doc, s.id, i, i + 1)}>↓</button>
                          <button className="px-1 text-neutral-400 hover:text-red-700" title={t("common.removeEntry")} onClick={() => wsHandle && removeMember(wsHandle.doc, s.id, i)}>×</button>
                        </>
                      )}
                    </div>
                  );
                })}
                {s.members.length === 0 && <p className="text-xs text-neutral-500">{t("seq.hint")}</p>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <form
          className="mt-2 flex gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (wsHandle && label.trim()) {
              createSequence(wsHandle.doc, label.trim());
              setLabel("");
            }
          }}
        >
          <Input placeholder={t("seq.new")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-sequence" />
          <Button type="submit" title={t("seq.create")}>
            <ListPlus size={14} />
          </Button>
        </form>
      )}
    </section>
  );
}
